import Button from "@/components/Button";
import SearchButton from "@/components/SearchButton";
import { ToolbarQuickSearchButton } from "@/components/SearchButton";
import TopActionBar from "@/components/TopActionBar";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageInteractionsBool,
  useCanManagePersonNotes,
  useCanManageTasksBool,
  useDenyAuthorization,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import useCurrentUser from "@/hooks/useCurrentUser";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import { Path, useNavigate } from "@/router";
import { generatePath } from "react-router";
import { SnackbarProvider } from "notistack";
import { snackbarProviderComponents } from "@/components/Snackbar";
import { useLocation } from "react-router-dom";
import { CreateGlobalInteractionSideSheet } from "./CreateGlobalInteraction/CreateGlobalInteractionSideSheet";
import CreateGlobalNoteSideSheet from "./CreateGlobalNote/CreateGlobalNoteSideSheet";
import CreateGlobalTaskSideSheet from "./CreateGlobalTask/CreateGlobalTaskSideSheet";
import Menu, { type MenuItem } from "@/components/Menu";
import KeyboardShortcutHint from "@/components/KeyboardShortcutHint";
import Sidebar from "./Sidebar";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import QuickSearchSideSheet from "./QuickSearchSideSheet";
import ActiveCallSideSheet from "./IncomingCallEventIntegration/ActiveCallSideSheet";
import IncomingCallEventPersonModal from "./IncomingCallEventIntegration/IncomingCallEventPersonModal";
import TopActionBarProvider from "./TopActionBarProvider";
import { SideBar, useTopActionBar } from "./TopActionBarContext";
import ErrorBannerProvider from "@/providers/ErrorBannerProvider";
import { NavLink } from "./types";
import { buildPrimaryNavTabItems, buildGoNavChordItems } from "./primaryNavTabItems";
import useGoNavChordMode from "@/hooks/useGoNavChordMode";
import { useQuestionMarkChatShortcut, useSlashShortcut } from "@/hooks/usePageSearchShortcut";
import { buildKeyboardShortcutEntries } from "@/lib/keyboardShortcutCatalog";
import classNames from "classnames";
import ScrollToTop from "@/components/ScrollToTop";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import useOpenGlobalAddMenuShortcut from "@/hooks/useOpenGlobalAddMenuShortcut";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import useAmplitude from "@/hooks/useAmplitude";
import { GLOBAL_SEARCH_ANALYTICS_EVENTS } from "./globalSearchAmplitude";
import { openTrackedGlobalAddMenuItem } from "@/lib/quickActionMenuAmplitude";
import { canAccessMcpPlayground } from "@/features/mcp-playground/access";
import AssumeUserBanner from "@/components/AssumeUserBanner";
import { GlobalQuickActionMenu } from "@/components/QuickActionMenu";
import { RosterAccessibleSectionsProvider } from "@/features/roster-activity";

const PRIMARY_NAV_DOMAINS = [DOMAIN.EVERYONE];

function GlobalAddButtonLabel() {
  const { isCompactLayout } = useIsCompactLayout();

  return (
    <span className="flex items-center gap-1">
      Add
      {!isCompactLayout ? (
        <KeyboardShortcutHint keyLabel="a" showModifier={false} testId="global-add-menu-shortcut-hint" />
      ) : null}
    </span>
  );
}

type AppLayoutProps<P extends Path> = {
  interior?: boolean;
  navLinks?: Record<string, NavLink<P>[]> | NavLink<P>[];
  BannerComponent?: React.ReactNode;
  bottomBar?: boolean;
};

function Main<P extends Path>({
  navLinks,
  BannerComponent,
  interior,
  bottomBar,
  children
}: React.PropsWithChildren<AppLayoutProps<P>>) {
  const { logout } = useCurrentUser();
  const user = useEffectiveDisplayUser();
  const { isCompactLayout } = useIsCompactLayout();
  const { showSidebar, toggleSidebar, currentSidebar } = useTopActionBar();
  const canManageInteractions = useCanManageInteractionsBool();
  const canManagePersonNotes = useCanManagePersonNotes();
  const canManageTasks = useCanManageTasksBool();
  const location = useLocation();
  const navigate = useNavigate();

  const { loaded, hasRole, hasUniversityAccess, universityLegacyIds } = useCapabilities();
  const denyAuthorization = useDenyAuthorization();
  const isUniversityStaff = hasRole(ROLE.UNIVERSITY_STAFF);
  const flags = useFlags();
  const showActivitiesScreen = !!flags["backoffice-activities-screen"];
  const { hasPrimarySection, primarySectionId } = usePrimarySection();
  const { trackEvent } = useAmplitude();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewMcpPlayground = canAccessMcpPlayground(canViewPeople, canViewFacilities);

  const openMcpPlayground = useCallback(() => {
    navigate("/dev/mcp-playground" as Path);
  }, [navigate]);

  useSlashShortcut();
  useQuestionMarkChatShortcut(canViewMcpPlayground ? openMcpPlayground : undefined);

  const openQuickSearch = useCallback(() => {
    trackEvent(GLOBAL_SEARCH_ANALYTICS_EVENTS.clickGlobalSearch);
    showSidebar(SideBar.QUICK_SEARCH)();
  }, [showSidebar, trackEvent]);

  const openGlobalAddMenuItem = useCallback(
    (dataTestId: string, openSidebar: () => void) => openTrackedGlobalAddMenuItem(dataTestId, openSidebar, trackEvent),
    [trackEvent]
  );

  const openGlobalInteraction = useCallback(() => {
    openGlobalAddMenuItem("add-interaction-menu-item", showSidebar(SideBar.CREATE_GLOBAL_INTERACTION))();
  }, [openGlobalAddMenuItem, showSidebar]);
  const openGlobalNote = useCallback(() => {
    openGlobalAddMenuItem("add-note-menu-item", showSidebar(SideBar.CREATE_GLOBAL_NOTE))();
  }, [openGlobalAddMenuItem, showSidebar]);
  const openGlobalTask = useCallback(() => {
    openGlobalAddMenuItem("add-task-menu-item", showSidebar(SideBar.CREATE_GLOBAL_TASK))();
  }, [openGlobalAddMenuItem, showSidebar]);

  const [globalAddMenuOpen, setGlobalAddMenuOpen] = useState(false);
  const [primaryNavMenuOpen, setPrimaryNavMenuOpen] = useState(false);

  const addInteractionLabel = isCompactLayout ? "Interaction" : "Add Interaction";
  const addNoteLabel = isCompactLayout ? "Note" : "Add Note";
  const addTaskLabel = isCompactLayout ? "Task" : "Add Task";

  const globalAddMenuItems = useMemo((): MenuItem[] => {
    return [
      ...(canManageInteractions
        ? [
            {
              label: addInteractionLabel,
              iconName: "3p" as const,
              onClick: openGlobalInteraction,
              dataTestId: "add-interaction-menu-item",
              chordKey: "i",
              useDivider: false
            }
          ]
        : []),
      ...(canManagePersonNotes
        ? [
            {
              label: addNoteLabel,
              iconName: "sticky_note_2" as const,
              onClick: openGlobalNote,
              dataTestId: "add-note-menu-item",
              chordKey: "n",
              useDivider: false
            }
          ]
        : []),
      ...(canManageTasks
        ? [
            {
              label: addTaskLabel,
              iconName: "inventory" as const,
              onClick: openGlobalTask,
              dataTestId: "add-task-menu-item",
              chordKey: "t",
              useDivider: false
            }
          ]
        : [])
    ];
  }, [
    addInteractionLabel,
    addNoteLabel,
    addTaskLabel,
    canManageInteractions,
    canManagePersonNotes,
    canManageTasks,
    openGlobalInteraction,
    openGlobalNote,
    openGlobalTask
  ]);

  const openGlobalAddMenu = useCallback(() => {
    if (globalAddMenuItems.length === 1) {
      globalAddMenuItems[0].onClick?.();
      return;
    }

    if (globalAddMenuItems.length > 1) {
      setGlobalAddMenuOpen(true);
    }
  }, [globalAddMenuItems]);

  useOpenGlobalAddMenuShortcut({
    onOpenAddMenu: openGlobalAddMenu,
    enabled: globalAddMenuItems.length > 0
  });

  useEffect(() => {
    if (!loaded) return;

    if (hasRole(ROLE.UNIVERSITY_STAFF)) {
      if (location.pathname.startsWith("/education/universities/")) {
        // Check if the user has access to this specific university

        // Get the university ID from the pathname
        const requestedUniversityId = location.pathname.split("/")[3];

        // Deny access if the user does not have access to this university
        if (!hasUniversityAccess(requestedUniversityId)) {
          if (universityLegacyIds.length > 0) {
            const universityId = universityLegacyIds[0];
            denyAuthorization(
              generatePath("/education/universities/:universityId/students", {
                universityId
              }) as Path
            );
          } else {
            denyAuthorization();
          }
        }
        // exit normally, allowing access to the university page
      } else {
        denyAuthorization();
      }
    }
  }, [loaded, hasRole, denyAuthorization, location.pathname, hasUniversityAccess, universityLegacyIds]);

  function createActions() {
    let actions = [];

    // Only show quick search for non-university staff
    if (!isUniversityStaff) {
      actions.push(
        <div key="toolbarQuickSearch" className="flex items-center">
          {isCompactLayout ? (
            <ToolbarQuickSearchButton onClick={openQuickSearch} testId="quick-search-button" />
          ) : (
            <SearchButton onClick={openQuickSearch} testId="quick-search-button" />
          )}
        </div>
      );
    }

    const menuItems = globalAddMenuItems;

    if (menuItems.length === 0) {
      return <>{actions}</>;
    }

    if (isCompactLayout) {
      actions.push(
        <div key="createGlobalAction">
          <GlobalQuickActionMenu
            menuItems={menuItems}
            menuOpen={globalAddMenuOpen}
            onMenuOpenChange={setGlobalAddMenuOpen}
            showOpenShortcutHint
          />
        </div>
      );
      return <>{actions}</>;
    }

    if (menuItems.length > 1) {
      const addButton = (
        <Button variant="squared" icon="add" testId="create-action-button" ariaKeyShortcuts="a">
          <GlobalAddButtonLabel />
        </Button>
      );
      actions.push(
        <div key="createGlobalAction">
          <Menu
            AnchorComponent={addButton}
            menuItems={menuItems}
            open={globalAddMenuOpen}
            onOpenChange={setGlobalAddMenuOpen}
          />
        </div>
      );
    } else if (menuItems.length === 1) {
      actions.push(
        <div key="createGlobalAction">
          <Button
            variant="squared"
            icon="add"
            onClick={menuItems[0].onClick}
            testId="create-action-button"
            ariaKeyShortcuts="a"
          >
            <GlobalAddButtonLabel />
          </Button>
        </div>
      );
    }

    return <>{actions}</>;
  }

  const hasSecondaryNav =
    navLinks && (Array.isArray(navLinks) ? navLinks.length > 0 : Object.keys(navLinks).length > 0);
  const shouldRenderSidebar = !isCompactLayout && hasSecondaryNav;

  const sectionsLink =
    hasPrimarySection && primarySectionId
      ? (`/sections/${primarySectionId}${isCompactLayout ? "" : "/jobs/list"}` as Path)
      : ("/sections" as Path);

  const primaryNavTabItems = buildPrimaryNavTabItems({
    pathname: location.pathname,
    sectionsLink,
    showActivitiesScreen
  });

  const primaryNavDomains = PRIMARY_NAV_DOMAINS;

  const goNavItems = useMemo(
    () => buildGoNavChordItems(primaryNavTabItems, primaryNavDomains, navigate),
    [primaryNavTabItems, primaryNavDomains, navigate]
  );

  const { goNavActive, deactivateGoNav } = useGoNavChordMode({
    items: goNavItems,
    enabled: !isUniversityStaff && goNavItems.length > 0,
    onCompactMenuOpen: isCompactLayout && !isUniversityStaff ? () => setPrimaryNavMenuOpen(true) : undefined,
    onCompactMenuClose: isCompactLayout && !isUniversityStaff ? () => setPrimaryNavMenuOpen(false) : undefined
  });

  const handlePrimaryNavMenuOpenChange = useCallback(
    (open: boolean) => {
      setPrimaryNavMenuOpen(open);
      if (!open) deactivateGoNav();
    },
    [deactivateGoNav]
  );

  const primaryNav =
    isCompactLayout && !isUniversityStaff
      ? {
          tabItems: primaryNavTabItems,
          domains: primaryNavDomains
        }
      : undefined;

  const primaryNavGoNav =
    !isUniversityStaff && goNavItems.length > 0
      ? {
          menuOpen: primaryNavMenuOpen,
          onMenuOpenChange: handlePrimaryNavMenuOpenChange,
          goNavActive
        }
      : undefined;

  const keyboardShortcutEntries = useMemo(
    () =>
      buildKeyboardShortcutEntries({
        showGoNav: !isUniversityStaff && goNavItems.length > 0,
        addMenuItems: globalAddMenuItems.flatMap((item) =>
          item.chordKey ? [{ label: String(item.label), chordKey: item.chordKey }] : []
        ),
        canViewMcpPlayground
      }),
    [canViewMcpPlayground, globalAddMenuItems, goNavItems.length, isUniversityStaff]
  );

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <TopActionBar
        {...{ user, logout, interior, hasSecondaryNav }}
        actions={createActions()}
        primaryNav={primaryNav}
        primaryNavGoNav={primaryNavGoNav}
        keyboardShortcutEntries={keyboardShortcutEntries}
      />
      <AssumeUserBanner />
      <main className={`${shouldRenderSidebar ? "grid grid-cols-[auto_1fr]" : "block"}`}>
        {shouldRenderSidebar && <Sidebar navLinks={navLinks} bottomBar={bottomBar} />}
        <div className="w-full min-w-0">
          <ErrorBannerProvider>
            {BannerComponent}
            <CreateGlobalInteractionSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_INTERACTION}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_INTERACTION)}
            />
            <CreateGlobalNoteSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_NOTE}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_NOTE)}
            />
            <CreateGlobalTaskSideSheet
              sidebarOpen={currentSidebar === SideBar.CREATE_GLOBAL_TASK}
              setSidebarOpen={toggleSidebar(SideBar.CREATE_GLOBAL_TASK)}
            />
            <QuickSearchSideSheet
              show={currentSidebar === SideBar.QUICK_SEARCH}
              onToggle={toggleSidebar(SideBar.QUICK_SEARCH)}
            />
            <IncomingCallEventPersonModal
              enabled={!!flags["backoffice-twilio-call-event-integration"] && !isUniversityStaff}
            />
            <ActiveCallSideSheet enabled={!!flags["backoffice-twilio-call-event-integration"] && !isUniversityStaff} />
            <div
              className={classNames({
                "mb-24": !isCompactLayout,
                "mx-4 lg:mx-12 xl:mx-24": !shouldRenderSidebar,
                "mr-4 lg:mr-12 xl:mr-24 ml-3 lg:ml-6 xl:ml-12": shouldRenderSidebar
              })}
            >
              {interior && !shouldRenderSidebar ? <div className="max-w-[1440px] mx-auto">{children}</div> : children}
            </div>
          </ErrorBannerProvider>
        </div>
      </main>
    </div>
  );
}

export default function AppLayout<P extends Path>(props: React.PropsWithChildren<AppLayoutProps<P>>) {
  return (
    <TopActionBarProvider>
      <SnackbarProvider
        Components={snackbarProviderComponents}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
      >
        <RosterAccessibleSectionsProvider>
          <Main {...props} />
        </RosterAccessibleSectionsProvider>
      </SnackbarProvider>
    </TopActionBarProvider>
  );
}
